import { useState } from "react";
import { Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useArchiveUser } from "@/hooks/use-archive-user";
import { usePermanentlyDeleteUserData } from "@/hooks/use-delete-user-data";

interface Props {
  userId: string;
  profileId: string | null;
  firstName: string;
  lastName: string;
  role: string;
  isSelf: boolean;
}

export function UserManagementActions({ userId, profileId, firstName, lastName, role, isSelf }: Props) {
  const queryClient = useQueryClient();
  const { archiveUser, restoreUser } = useArchiveUser();
  const deleteUser = usePermanentlyDeleteUserData();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const isArchived = role === "archived";
  const isSystem = role === "superuser" || role === "partner";
  const canArchive = !isSelf && !isSystem && !!profileId;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["organization-users"] });

  const saveName = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ first_name: first.trim() || null, last_name: last.trim() || null })
      .eq("user_id", userId);
    setSaving(false);
    if (error) {
      toast.error("Failed to update name");
      return;
    }
    toast.success("Name updated");
    setEditOpen(false);
    refresh();
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className="flex items-center gap-1" onClick={stop}>
      <Button variant="ghost" size="sm" onClick={() => { setFirst(firstName); setLast(lastName); setEditOpen(true); }} title="Edit name">
        <Pencil className="h-4 w-4" /><span className="sr-only">Edit name</span>
      </Button>

      {canArchive && !isArchived && (
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setArchiveOpen(true)} title="Archive user">
          <Archive className="h-4 w-4" /><span className="sr-only">Archive user</span>
        </Button>
      )}
      {canArchive && isArchived && (
        <>
          <Button variant="ghost" size="sm" onClick={() => setRestoreOpen(true)} title="Restore user">
            <ArchiveRestore className="h-4 w-4" /><span className="sr-only">Restore user</span>
          </Button>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)} title="Permanently delete">
            <Trash2 className="h-4 w-4" /><span className="sr-only">Permanently delete</span>
          </Button>
        </>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" onClick={stop}>
          <DialogHeader><DialogTitle>Edit name</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`first-${userId}`}>First name</Label>
              <Input id={`first-${userId}`} value={first} onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`last-${userId}`}>Surname</Label>
              <Input id={`last-${userId}`} value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveName} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this user?</AlertDialogTitle>
            <AlertDialogDescription>
              They will be signed out and unable to log in. Their role is kept and can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`reason-${userId}`}>Reason (optional)</Label>
            <Input id={`reason-${userId}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Left the company" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReason("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiveUser.isPending}
              onClick={() => profileId && archiveUser.mutate(
                { contactId: profileId, userId, reason: reason.trim() || undefined },
                { onSuccess: () => { setReason(""); refresh(); } },
              )}
            >Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this user?</AlertDialogTitle>
            <AlertDialogDescription>Their previous role is restored and they can log in again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={restoreUser.isPending}
              onClick={() => profileId && restoreUser.mutate({ contactId: profileId, userId }, { onSuccess: refresh })}
            >Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes their profile, role, camera access and email subscriptions. Order and audit history is kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteUser.isPending}
              onClick={() => deleteUser.mutate(userId, { onSuccess: refresh })}
            >Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
